// @flow

import Immutable from 'immutable';
import {
  fromJS,
  type RecordFactory,
  type RecordInstance,
  Record,
} from 'immutable';

import {
  defaultFetchDataFormat,
  type fetchDataFormatType,
} from 'app/utils/redux';

import {type ActivityType} from 'app/activity/types';
import type {UserType, BasicUserType, InvitationType} from 'app/user/types';
import {hasCurrentUserData} from 'app/current-user/api';
import {
  RECEIVE_ACTIVITY_LIST,
  RECEIVE_INVITATION_LIST,
  RECEIVE_DELETE_INVITATION,
  RECEIVE_USER_DETAILS,
  RECEIVE_USER_LIST,
  type UsersActions,
} from './actions';

import {
  setNormalizedList,
  getRequestResponseMapFactory,
  type RequestResponseMapType,
} from '../utils/redux_v2';

type StateShape = {
  activities: RecordInstance<RequestResponseMapType<ActivityType>>,
  invitations: RecordInstance<RequestResponseMapType<InvitationType>>,
  usersv2: RecordInstance<RequestResponseMapType<UserType>>,
  users: fetchDataFormatType<UserType>,
  basicUsers: {[string]: BasicUserType},
};

const StateFactory: RecordFactory<StateShape> = Record({
  activities: getRequestResponseMapFactory<ActivityType>(),
  invitations: getRequestResponseMapFactory<InvitationType>(),
  usersv2: getRequestResponseMapFactory<UserType>(),
  users: fromJS(defaultFetchDataFormat()),
  basicUsers: fromJS({}),
});

export type RecordOfUsersState = RecordInstance<StateShape>;

const userArea = (
  state: RecordOfUsersState = StateFactory(),
  action: UsersActions
): RecordOfUsersState => {
  if (hasCurrentUserData(action)) {
    const user = Immutable.fromJS(action.payload);
    const strId = action.payload.id.toString();

    return state
      .setIn(['users', 'byId', strId], user)
      .setIn(['usersv2', 'byId', strId], user);
  }

  switch (action.type) {
    case RECEIVE_ACTIVITY_LIST: {
      return state.set(
        'activities',
        setNormalizedList(
          action.query,
          action.response,
          state.get('activities')
        )
      );
    }

    case RECEIVE_INVITATION_LIST:
      return state.set(
        'invitations',
        setNormalizedList(
          action.query,
          action.response,
          state.get('invitations')
        )
      );

    case RECEIVE_DELETE_INVITATION:
      return state.setIn(
        ['invitations', 'byId', action.id.toString(), 'status'],
        'rejected'
      );

    case RECEIVE_USER_DETAILS: {
      const user = Immutable.fromJS(action.data);
      const strId = action.data.id.toString();

      return state
        .setIn(['users', 'byId', strId], user)
        .setIn(['usersv2', 'byId', strId], user);
    }

    case RECEIVE_USER_LIST:
      return state.set(
        'usersv2',
        setNormalizedList(action.query, action.response, state.get('usersv2'))
      );

    default:
      return state;
  }
};

export default userArea;
